import { useEffect } from "react";
import { useQuery, useMutation, useQueryClient, type UseMutationOptions } from "@tanstack/react-query";
import { useAppConfig } from "providers";
import { useSessionFlag } from "hooks/useSessionFlag";
import { IncidentReportController } from "../controllers/incidentReportController";
import { type ApiError } from "api/error/ApiError";
import { onError } from "api/error/errorHandler";
import { type CICaseDetail, type CIReferral, type IncidentReport } from "components/CriticalIncident/interfaces";
import { type IncidentReportUpdatePayload } from "api/types";
import { useFeatureManager } from "hooks/useFeatureManager";
import { COUNTRY_CODE, useProviderCountryCode } from "utils/regionUtils";
import { enableMockServiceWorker } from "@/mocks/browser";

// Initialize controller with API host, defaulting every hook to v1. AU has no live v2
// backend yet (see IncidentReportController.resolveApiHost's mock-routing bridge), so only
// the specific hooks/call sites that actually need AU on v2 resolve that for themselves —
// see useGetIncidentReports/useGetIncidentReport below, and the per-section overrides in
// IncidentReportTab.tsx. Every hook still accepts an optional explicit `version` override,
// which — when given — wins over whatever default it resolves to.
const useController = () => {
    const { REACT_APP_INCIDENT_REPORT_HOST } = useAppConfig();
    const isMockMode = useSessionFlag<boolean | undefined>('use-mock-server');
    const { isCriticalIncidentFeatureEnabled } = useFeatureManager();
    const isAU = useProviderCountryCode() === COUNTRY_CODE.AUSTRALIA;

    // Ensure a mock backend is actually running for AU, since v2 always routes there
    // regardless of the 'use-mock-server' flag.
    useEffect(() => {
        if (isAU) {
            enableMockServiceWorker();
        }
    }, [isAU]);

    return new IncidentReportController(REACT_APP_INCIDENT_REPORT_HOST, 'v1', isMockMode, isCriticalIncidentFeatureEnabled());
};

// Query hooks
export const useGetIncidentReports = (caseId: number, activityId: number, version?: string, enabled = true) => {
    const controller = useController();
    const isAU = useProviderCountryCode() === COUNTRY_CODE.AUSTRALIA;
    const resolvedVersion = version ?? (isAU ? 'v2' : undefined);
    return useQuery({
        queryKey: ['incidentReports', caseId, activityId],
        queryFn: () => controller.getIncidentReports({ caseId, activityId, version: resolvedVersion }),
        enabled: enabled && !!caseId && !!activityId,
    });
};

export const useGetIncidentReport = (caseId: number, activityId: number, id: string, version?: string) => {
    const controller = useController();
    const isAU = useProviderCountryCode() === COUNTRY_CODE.AUSTRALIA;
    const resolvedVersion = version ?? (isAU ? 'v2' : undefined);
    return useQuery({
        queryKey: ['incidentReports', caseId, activityId, id],
        queryFn: () => controller.getIncidentReport({ caseId, activityId, id, version: resolvedVersion }),
        enabled: !!caseId && !!activityId && !!id,
    });
};

export const useGetCriticalIncidentCases = (version?: string) => {
    const controller = useController();
    return useQuery<any[]>({
        queryKey: ['criticalIncident', 'cases'],
        queryFn: () => controller.getCriticalIncidentCases(version),
    });
};

export const useGetCriticalIncidentCase = (caseId: number, activityId: number, version?: string) => {
    const controller = useController();
    return useQuery<CIReferral|CICaseDetail>({
        queryKey: ['criticalIncident', 'cases', caseId, activityId],
        queryFn: () => controller.getCriticalIncidentCase({ caseId, activityId, version }),
        enabled: !!caseId && !!activityId,
    });
};

// Mutation hooks
export const useCreateIncidentReport = (
    options?: UseMutationOptions<string, ApiError, { caseId: number; activityId: number; payload: Partial<IncidentReport> }>,
    version?: string
) => {
    const controller = useController();
    const queryClient = useQueryClient();
    const { onSuccess, onError: onErrorOption, ...restOptions } = options ?? {};
    return useMutation({
        mutationFn: ({ caseId, activityId, payload }) => controller.createIncidentReport({ caseId, activityId, payload, version }),
        mutationKey: ['incidentReports', 'create'],
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ['incidentReports', variables.caseId, variables.activityId] });
            onSuccess?.(data, variables, context);
        },
        ...restOptions,
        onError: typeof onErrorOption === 'function' ? onErrorOption : onError
    });
};

export const useUpdateIncidentReport = (
    options?: UseMutationOptions<IncidentReport, ApiError, { caseId: number; activityId: number; id: string; payload: IncidentReportUpdatePayload }>,
    version?: string
) => {
    const controller = useController();
    const queryClient = useQueryClient();
    const { onSuccess, onError: onErrorOption, ...restOptions } = options ?? {};
    return useMutation({
        mutationFn: ({ caseId, activityId, id, payload }) => controller.updateIncidentReport({ caseId, activityId, id, payload, version }),
        mutationKey: ['incidentReports', 'update'],
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ['incidentReports', variables.caseId, variables.activityId] });
            onSuccess?.(data, variables, context);
        },
        ...restOptions,
        onError: typeof onErrorOption === 'function' ? onErrorOption : onError
    });
};

export const useSubmitIncidentReport = (
    options?: UseMutationOptions<IncidentReport, ApiError, { caseId: number; activityId: number; id: string, payload: IncidentReport }>,
    version?: string
) => {
    const controller = useController();
    const queryClient = useQueryClient();
    const { onSuccess, onError: onErrorOption, ...restOptions } = options ?? {};
    return useMutation({
        mutationFn: ({ caseId, activityId, id, payload }) => controller.submitIncidentReport({ caseId, activityId, id, payload, version }),
        mutationKey: ['incidentReports', 'submit'],
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ['incidentReports', variables.caseId, variables.activityId] });
            onSuccess?.(data, variables, context);
        },
        ...restOptions,
        onError: typeof onErrorOption === 'function' ? onErrorOption : onError
    });
};
